<template>
  <div class="near-by-container">
    <div class="near-by-title">
      <div class="title-text">
        <span class="heading3">{{ $t('experiences_near_by') }}</span>
      </div>
    </div>
    <div class="near-by-map" v-if="markers?.length">
      <GoogleMap
        :api-key="config.public.googleApiKey"
        class="custom-google-map"
        map-id="nearByMap"
        :center="center"
        :fullscreenControl="false"
        :mapTypeControl="false"
        :streetViewControl="false"
        :zoomControl="true"
        :rotateControl="false"
        :scaleControl="false"
        :zoom="zoom"
      >
        <MarkerCluster>
          <Marker
            v-for="marker in markers"
            :key="marker.uuid"
            :options="{
              position: marker.coordinates,
              icon:
                markerSelected?.uuid === marker.uuid
                  ? '/markers/pin-palc-selected.png'
                  : '/markers/pin-palc.png'
            }"
            @click="setMarkerSelected(marker)"
          >
          </Marker>
        </MarkerCluster>
        <div v-if="markerSelected" class="marker-info-window">
          <div class="info-window-content">
            <div
              class="image"
              :class="markerSelected.experience.image ? 'with-image' : ''"
            >
              <img
                v-if="markerSelected.experience.image"
                :src="markerSelected.experience.image"
              />
              <img
                v-else
                src="/logos/PALC_Marque_Gold.svg"
                alt="logo"
                class="logo-img"
              />
            </div>
            <div class="text">
              <div class="info-window-title">
                <span class="heading6">{{
                  markerSelected.experience.title
                }}</span>
              </div>
              <div class="info-window-date">
                <span
                  class="caption"
                  v-if="markerSelected.experience.location"
                  >{{ markerSelected.experience.location }}</span
                >
                <span class="caption" v-if="markerSelected.experience.date">{{
                  markerSelected.experience.date
                }}</span>
              </div>
            </div>
          </div>
        </div>
      </GoogleMap>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import 'moment/dist/locale/es'
import 'moment/dist/locale/en-gb'
import 'moment/dist/locale/fr'
import 'moment/dist/locale/de'
import 'moment/dist/locale/sv'
import 'moment/dist/locale/ar'
import { ElLoading, ElMessageBox } from 'element-plus'
import { useI18n } from 'vue-i18n'
import { GoogleMap, Marker, MarkerCluster, InfoWindow } from 'vue3-google-map'

export default defineComponent({
  name: 'NearBy',
  components: {
    GoogleMap,
    Marker,
    MarkerCluster,
    InfoWindow
  },
  setup() {
    const { width, height } = useBreakpoints()
    const { t } = useI18n()
    const { $sentry } = useNuxtApp()
    const router = useRouter()
    const route = useRoute()
    const config = useRuntimeConfig()

    const initialVariables = [getGoogleInitialVariables()]

    let objectToReturn = {
      width,
      height,
      t,
      router,
      route,
      $sentry,
      config
    }
    for (const variable of initialVariables) {
      objectToReturn = Object.assign(objectToReturn, variable)
    }

    return objectToReturn
  },
  data() {
    return {
      experiences: null,
      markerSelected: null,
      isFullscreen: false,
      zoom: 6,
      center: this.defaultCenter
    }
  },
  computed: {
    language() {
      return getAuthUser()?.value?.language
        ? getAuthUser()?.value?.language
        : this.$i18n.locale
    },
    markers() {
      if (this.experiences?.getAllExperiences?.data?.length) {
        return this.experiences?.getAllExperiences?.data
          .filter((experience) => experience.location?.address)
          .map((experience) => {
            return {
              uuid: experience.uuid,
              coordinates: {
                lat: experience.location.address.latitude,
                lng: experience.location.address.longitude
              },
              experience: {
                uuid: experience.uuid,
                title:
                  this.$i18n.locale === 'es'
                    ? experience.name_es
                    : this.$i18n.locale === 'en'
                    ? experience.name_en
                    : this.$i18n.locale === 'fr'
                    ? experience.name_fr
                    : this.$i18n.locale === 'de'
                    ? experience.name_de
                    : this.$i18n.locale === 'sv'
                    ? experience.name_sv
                    : this.$i18n.locale === 'ar'
                    ? experience.name_ar
                    : experience.name,
                image: experience.mainImage?.url ?? null,
                location: experience.location?.name ?? null,
                date: this.getExperienceDate(experience)
              }
            }
          })
      }
      return []
    }
  },
  mounted() {
    this.getExperiences()
    document.addEventListener('fullscreenchange', () => {
      this.markerSelected = null
    })
  },
  methods: {
    async getExperiences(refetch = false) {
      try {
        this.loadingDialog = ElLoading.service({
          fullscreen: true,
          background: '#F7F7F7',
          svg: useLoaderSvg(),
          svgViewBox: useLoaderSvgViewBox()
        })
        this.experiences = await apolloExperiencesForMap(
          {
            sortBy: 'CREATED_AT',
            sortDesc: true,
            search: null,
            page: 1,
            first: 10
          },
          true,
          {
            filterField: {
              filterEnabled: {
                value: true
              }
            }
          },
          refetch ? 'network-only' : null
        )
      } catch (err) {
        this.$showError(err, this.t)
        this.$sentry(err, 'getExperiences')
      } finally {
        this.init()
        this.loadingDialog.close()
        this.$emit('calculateOffset')
      }
    },
    getExperienceDate(experience) {
      if (!experience.start_date || !experience.end_date) {
        return null
      }
      moment.locale(this.language)
      const startDate = moment(experience.start_date)
      const endDate = moment(experience.end_date)
      if (startDate.isSame(endDate, 'month')) {
        return ` / ${startDate.format('DD')} - ${endDate.format('DD MMMM')}`
      }
      return ` / ${startDate.format('DD MMMM')} -
              ${endDate.format('DD MMMM')}`
    },
    setMarkerSelected(event) {
      if (event?.uuid === this.markerSelected?.uuid) {
        this.markerSelected = null
      } else {
        this.markerSelected = event
      }
    },
    init() {
      this.setBounds(this, this.markers)
      this.center = this.$refs.googleMap?.map?.getCenter()
        ? {
            lat: this.$refs.googleMap?.map.getCenter().lat(),
            lng: this.$refs.googleMap?.map.getCenter().lng()
          }
        : this.defaultCenter
      this.zoom = this.$refs.googleMap?.map?.getZoom()
        ? this.$refs.googleMap?.map.getZoom()
        : 6
    }
  }
})
</script>

<style scoped lang="scss">
.near-by-container {
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: left;
  background-color: var(--brand-off-white);
  .near-by-title {
    width: 100%;
    text-align: left;
    margin-bottom: 24px;
    display: block;
    position: relative;
    @media (min-width: 1100px) {
      padding-left: 96px;
      padding-right: 96px;
    }
    @media (max-width: 1100px) {
      padding-left: 24px;
      padding-right: 24px;
    }
    .title-text {
      display: inline-block;
      .heading3 {
        color: var(--brand-off-black);
      }
    }
  }
  .near-by-map {
    width: 100%;
    height: 520px;
    position: relative;
    :deep(.custom-google-map) {
      height: 520px;
      .marker-info-window {
        width: 327px;
        height: 122px;
        padding: 0 !important;
        background-color: var(--brand-white);
        position: absolute;
        @media (min-width: 1100px) {
          bottom: 32px;
        }
        @media (max-width: 1100px) {
          bottom: 24px;
        }
        left: calc(50vw - 163px);
        .info-window-content {
          position: relative;
          .image {
            position: absolute;
            top: 0;
            width: 120px;
            height: 120px;
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: var(--action-hover);
            img {
              width: 119px;
              height: 119px;
            }
            &:not(.with-image) {
              img {
                width: 40px;
                height: 40px;
              }
            }
          }
          .text {
            width: calc(100% - 128px);
            position: absolute;
            left: 128px;
            .info-window-title {
              margin-top: 12px;
              max-width: 241px;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
              height: 26px;
              .heading6 {
                color: var(--brand-off-black);
              }
            }
            .info-window-date {
              margin-top: 8px;
            }
            .info-window-price {
              margin-top: 4px;
              height: 16px;
            }
          }
        }
      }
    }
  }
}
</style>